/** Max length for For You section display titles (Backend Joi / schema). */
export const SECTION_TITLE_MAX = 200;

export const SECTION_TITLE_FIELDS = [
  'feeling_title',
  'quick_sessions_title',
  'yoga_nidra_title',
  'deity_affinity_title',
];

export const emptySectionTitles = () => ({
  feeling_title: '',
  quick_sessions_title: '',
  yoga_nidra_title: '',
  deity_affinity_title: '',
});

/** Load titles from GET/PUT data — missing/null → empty string for controlled inputs. */
export const sectionTitlesFromConfig = (data) => {
  const out = emptySectionTitles();
  if (!data || typeof data !== 'object') return out;
  for (const key of SECTION_TITLE_FIELDS) {
    const raw = data[key];
    out[key] = raw == null ? '' : String(raw);
  }
  return out;
};

/**
 * Blank / whitespace → null (clear). Non-empty → trimmed string.
 * Matches Backend normalizeOptionalSectionTitle when a value is sent.
 */
export const normalizeSectionTitleForPayload = (raw) => {
  const trimmed = String(raw ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

export const sectionTitlesPayload = (formLike) => {
  const out = {};
  for (const key of SECTION_TITLE_FIELDS) {
    out[key] = normalizeSectionTitleForPayload(formLike?.[key]);
  }
  return out;
};

const SECTION_TITLE_REQUIRED_MESSAGES = {
  feeling_title: 'Feeling section title is required',
  quick_sessions_title: 'Quick Sessions section title is required',
  yoga_nidra_title: 'Yoga Nidra section title is required',
  deity_affinity_title: 'Deity Affinity section title is required',
};

/** Client-side required + max-length check for section titles. */
export const validateSectionTitles = (formLike) => {
  const errs = {};
  for (const key of SECTION_TITLE_FIELDS) {
    const trimmed = String(formLike?.[key] ?? '').trim();
    if (!trimmed) {
      errs[key] = SECTION_TITLE_REQUIRED_MESSAGES[key] || 'Section title is required';
    } else if (trimmed.length > SECTION_TITLE_MAX) {
      errs[key] = `Must be at most ${SECTION_TITLE_MAX} characters`;
    }
  }
  return errs;
};

/**
 * Build PUT body fields for section titles + category IDs.
 * Does not include greeting_slots or unknown properties.
 */
export const buildCategoryAndTitlePayload = (formData) => {
  const feeling = String(formData.feeling_main_category_id || '').trim();
  const quick = String(formData.quick_sessions_main_category_id || '').trim();
  const yoga = String(formData.yoga_nidra_sub_category_id || '').trim();
  const deity = String(formData.deity_affinity_main_category_id || '').trim();

  return {
    feeling_main_category_id: feeling,
    quick_sessions_main_category_id: quick,
    yoga_nidra_sub_category_id: yoga,
    deity_affinity_main_category_id: deity,
    ...sectionTitlesPayload(formData),
  };
};

/** Mongo ObjectId string — mirrors Backend Joi objectIdString for greeting slot _id. */
export const OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

/**
 * New greeting slot form row. No `_id` — Backend generates one on first save
 * (merge-by-id treats missing `_id` as a new slot).
 */
export const emptyGreetingSlot = () => ({
  start_time: '',
  end_time: '',
  title: '',
  description: '',
  bg_image: '',
  bg_audio: '',
});

/**
 * Map GET/PUT greeting_slots into form state.
 * Preserves stable `_id` when present (Backend Phase 1 subdocument ids).
 */
export const normalizeSlotsFromServer = (slots) => {
  if (!Array.isArray(slots) || !slots.length) return [emptyGreetingSlot()];
  return slots.map((s) => {
    const row = {
      start_time: s?.start_time || '',
      end_time: s?.end_time || '',
      title: s?.title || '',
      description: s?.description || '',
      bg_image: s?.bg_image || '',
      bg_audio: s?.bg_audio || '',
    };
    const id = s?._id != null ? String(s._id).trim() : '';
    if (id && OBJECT_ID_RE.test(id)) {
      row._id = id;
    }
    return row;
  });
};

/**
 * Build PUT greeting_slots array for Backend merge-by-`_id`:
 * - Include `_id` for every existing slot (round-trip).
 * - Omit `_id` only for genuinely new rows (no id in form state).
 * Array order is the schedule order (reorders keep identity via `_id`).
 */
export const buildGreetingSlotsPayload = (slots) =>
  (Array.isArray(slots) ? slots : []).map((s) => {
    const audio = String(s?.bg_audio || '').trim();
    const payload = {
      start_time: String(s?.start_time || '').trim(),
      end_time: String(s?.end_time || '').trim(),
      title: String(s?.title || '').trim(),
      description: String(s?.description || '').trim(),
      bg_image: String(s?.bg_image || '').trim(),
      bg_audio: audio ? audio : null,
    };
    const id = s?._id != null ? String(s._id).trim() : '';
    if (id && OBJECT_ID_RE.test(id)) {
      payload._id = id;
    }
    return payload;
  });
